/**
 * Constantes e tipos i18n — Node-safe (zero side-effects).
 *
 * Esse módulo NÃO importa i18next nem detector — pode ser importado por
 * scripts `tsx` (prerender, generate-seo-files, landing-pages.config) sem
 * disparar o i18n.init(), que tem deps de browser (localStorage/navigator)
 * e import.meta.env-só-em-Vite.
 *
 * src/i18n/index.ts re-exporta tudo daqui, então imports antigos
 * (`from '../i18n'`) continuam funcionando no código de browser.
 */
export const SUPPORTED_LNGS = ['pt-BR', 'en', 'es'] as const;
export type AppLocale = (typeof SUPPORTED_LNGS)[number];

export const NAMESPACES = [
  'common', 'landing', 'institutional', 'blog', 'auth', 'pool', 'admin',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

/**
 * Lê uma env tanto sob Vite (import.meta.env) quanto sob Node (process.env)
 * — sem quebrar quando `import.meta.env` não existe (tsx em scripts Node).
 *
 * IMPORTANTE 1: o Vite só substitui `import.meta.env.VITE_*` no build quando o
 * acesso é ESTÁTICO (literal `import.meta.env.VITE_FOO`). Acesso dinâmico
 * (`im.env[key]`) NÃO é trocado — no bundle de produção retorna undefined e
 * tudo cai no fallback. Por isso cada chave tem leitor próprio com o acesso
 * direto (idêntico ao padrão usado em `services/api.ts`).
 *
 * IMPORTANTE 2: sob `tsx` (scripts Node), `import.meta.env` pode existir como
 * objeto vazio em vez de lançar exception — acesso a property então retorna
 * `undefined` SEM disparar o catch. O bug 2026-05-25 era esse: o try retornava
 * undefined silenciosamente e o `nodeEnv` fallback nunca executava, fazendo
 * prerender ver `I18N_ROUTING_ENABLED=false` mesmo com env var setada no CI.
 * Por isso cada reader: tenta o acesso estático, e SÓ retorna o valor do Vite
 * se for não-undefined; senão cai pro `nodeEnv()`.
 */
function nodeEnv(key: string): string | undefined {
  // Sem @types/node aqui (tsconfig do app é DOM-only). Cast pontual evita
  // dep extra só pra essa leitura defensiva sob `tsx`.
  const g = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  return g?.env?.[key];
}

function readApiUrl(): string | undefined {
  try {
    const v = import.meta.env.VITE_API_URL;
    if (v !== undefined) return v;
  } catch {
    /* import.meta.env indisponível sob tsx — segue p/ process.env */
  }
  return nodeEnv('VITE_API_URL');
}

function readI18nRoutingFlag(): string | undefined {
  try {
    const v = import.meta.env.VITE_I18N_ROUTING_ENABLED;
    if (v !== undefined) return v;
  } catch {
    /* import.meta.env indisponível sob tsx — segue p/ process.env */
  }
  return nodeEnv('VITE_I18N_ROUTING_ENABLED');
}

function readSiteDomain(): string | undefined {
  try {
    const v = import.meta.env.VITE_SITE_DOMAIN;
    if (v !== undefined) return v;
  } catch {
    /* import.meta.env indisponível sob tsx — segue p/ process.env */
  }
  return nodeEnv('VITE_SITE_DOMAIN');
}

function readCookieDomain(): string | undefined {
  try {
    const v = import.meta.env.VITE_COOKIE_DOMAIN;
    if (v !== undefined) return v;
  } catch {
    /* import.meta.env indisponível sob tsx */
  }
  return nodeEnv('VITE_COOKIE_DOMAIN');
}

function readLangCookieName(): string | undefined {
  try {
    const v = import.meta.env.VITE_LANG_COOKIE_NAME;
    if (v !== undefined) return v;
  } catch {
    /* import.meta.env indisponível sob tsx */
  }
  return nodeEnv('VITE_LANG_COOKIE_NAME');
}

/** Feature flag das rotas multilíngue. Build-time. Default OFF. */
export const I18N_ROUTING_ENABLED = readI18nRoutingFlag() === 'true';

/** URL base da API (lida no init do i18next; idêntico fallback ao Vite). */
export const API_URL = readApiUrl() || 'http://localhost:8080/api';

/**
 * Domínio onde o app está deployado (ex.: 'dev.bolaoelite.com.br' em DEV,
 * 'bolaoelite.com.br' em PROD) — definido em build-time pelo CI/CD.
 * Usado por utilitários de domain.
 */
export const SITE_DOMAIN = readSiteDomain();

/**
 * Domínio do cookie de idioma (ex.: 'bolaoelite.com.br'). Em produção e DEV
 * tem que ser o domínio RAIZ pra cobrir todos os subdomínios — `dev.*`,
 * `dev.app.*`, `app.*` e a raiz só compartilham `bolaoelite.com.br`. Em
 * dev local fica undefined → cookie no host atual.
 */
export const COOKIE_DOMAIN = readCookieDomain();

/**
 * Nome do cookie de idioma. Como DEV e PROD compartilham o mesmo
 * COOKIE_DOMAIN raiz, usar nomes diferentes evita que a escolha de idioma
 * num ambiente afete o outro. CI seta 'bolao-lang-dev' em DEV;
 * default 'bolao-lang' em PROD e dev local.
 */
export const LANG_COOKIE_NAME = readLangCookieName() || 'bolao-lang';

export const SITE_URL = 'https://bolaoelite.com.br';
